import React, { useCallback, useEffect, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Button,
  Box,
  FormHelperText,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "../../../styles/social/social.module.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { get, put } from "../../../utils/api";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import SocialLayout from "../../../Layout/SocialLayout";
import WestIcon from "@mui/icons-material/West";
import { textError } from "../../../utils/validationMessage";
import FileUpload from "../../GovernanceDashboard/DataEntry/FileUpload";

const SOCIAL_PRIMARY = "#1C486B";
const socialRadioSx = {
  color: SOCIAL_PRIMARY,
  "&.Mui-checked": { color: SOCIAL_PRIMARY },
};
const backButtonSx = {
  color: SOCIAL_PRIMARY,
  textTransform: "none",
  border: `1px solid ${SOCIAL_PRIMARY}`,
  "&:hover": { borderColor: SOCIAL_PRIMARY },
};
const primaryButtonSx = {
  textTransform: "none",
  backgroundColor: SOCIAL_PRIMARY,
  "&:hover": { backgroundColor: "#163a56" },
};

const removeCommas = (val) => {
  if (val === null || val === undefined) return "";
  return val.toString().replace(/,/g, "").trim();
};

const formatWithCommas = (val) => {
  const normalized = removeCommas(val);
  if (normalized === "") return "";
  if (!/^\d*\.?\d*$/.test(normalized)) return normalized;
  const [integerPart = "", decimalPart] = normalized.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

const stripCommasFromNumericStrings = (input) => {
  if (Array.isArray(input)) return input.map(stripCommasFromNumericStrings);
  if (input && typeof input === "object") {
    return Object.fromEntries(
      Object.entries(input).map(([key, value]) => [
        key,
        stripCommasFromNumericStrings(value),
      ]),
    );
  }
  if (typeof input === "string" && /^\d{1,3}(,\d{3})*(\.\d+)?$/.test(input)) {
    return removeCommas(input);
  }
  return input;
};

const BooleanRadioGroup = ({ label, name, value, onChange }) => (
  <FormControl component="fieldset" style={{ marginBottom: "1rem" }}>
    <p className={styles.form_heading} style={{ fontWeight: "bold",marginTop:"1rem"  }}>
      {label}
    </p>
    <RadioGroup
      row
      name={name}
      value={value === false || value === "false" ? "false" : "true"}
      onChange={onChange}
      sx={{ pl: 2 }}
    >
      <FormControlLabel value="true" control={<Radio sx={socialRadioSx} />} label="Yes" />
      <FormControlLabel value="false" control={<Radio sx={socialRadioSx} />} label="No" />
    </RadioGroup>
  </FormControl>
);

const CustomerSatisfaction = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [module, setModule] = useState("monthly");
  const [errors, setErrors] = useState({});
  const [entryMeta, setEntryMeta] = useState({
    companyId: null,
    year: null,
    month: null,
  });
  const [totalQuantity, setTotalQuantity] = useState(null);
  
  // Annual form data
  const [annualFormData, setAnnualFormData] = useState({
    grievanceMechanism: { status: true, remark: "", documentUrl: "" },
    dataPrivacyCompliance: true,
  });

  // Monthly form data
  const [monthlyFormData, setMonthlyFormData] = useState({
    customerComplaints: "",
    complaintsResolved: "",
    productRecalls: "",
    productRecallRate: "",
    safetyIncidentsReported: "",
    dataBreaches: "",
    dataBreachRate: "",
    marketingComplaints: "",
    additionalDetails: "",
  });

  const validateForm = () => {
    let hasErrors = false;
    const errorsObj = {};
    const formData = module === "annual" ? annualFormData : monthlyFormData;

    const isEmpty = (value) =>
      value === "" || value === null || value === undefined;

    if (module === "annual") {
      // Validate annual form data
      Object.entries(formData).forEach(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          // Special handling for grievanceMechanism
          if (key === "grievanceMechanism") {
            if (value.status === true) {
              const grievanceErrors = {};
              if (!value.remark || value.remark.trim() === "") {
                grievanceErrors.remark = textError;
                hasErrors = true;
              }
              if (!value.documentUrl || value.documentUrl.trim() === "") {
                grievanceErrors.documentUrl = textError;
                hasErrors = true;
              }
              if (Object.keys(grievanceErrors).length > 0) {
                errorsObj[key] = grievanceErrors;
              }
            }
          }
        }
        // Booleans are valid as long as they're true or false
      });
    } else {
      // Validate monthly form data
      Object.entries(formData).forEach(([key, value]) => {
        if (typeof value === "string" || typeof value === "number") {
          if (isEmpty(value)) {
            errorsObj[key] = textError;
            hasErrors = true;
          }
        }
      });
    }

    setErrors(errorsObj);
    return hasErrors;
  };

  const fetchData = useCallback(async (id) => {
    try {
      const response = await get(`/social/getSocial?id=${id}`);
      const responseData = response?.data?.data;
      const socialData = Array.isArray(responseData) ? responseData[0] : responseData;
      const fetchedData = socialData?.customerSatisfaction || {};

      // Get module from URL params, API response, or default to monthly
      const urlModule = searchParams.get("entryType");
      const apiModule = socialData?.module || socialData?._socialDataRef?.module;
      let finalModule = "monthly";

      if (urlModule === "annual" || apiModule === "Annual" || apiModule === "annual") {
        finalModule = "annual";
      } else if (urlModule === "monthly" || apiModule === "Monthly" || apiModule === "monthly") {
        finalModule = "monthly";
      }

      setModule(finalModule);

      // Capture meta needed for dashboard/totalProduction (companyId, year, month)
      const companyId = socialData?.company || socialData?._socialDataRef?.company || null;
      const yearToSubmit = socialData?._socialDataRef?.yearToSubmit ?? null;
      const monthToSubmit = socialData?._socialDataRef?.monthToSubmit ?? null;
      setEntryMeta({
        companyId,
        year: yearToSubmit,
        month: monthToSubmit,
      });

      // Separate annual and monthly data
      const annualData = {};
      const monthlyData = {};

      if (fetchedData) {
        // Annual fields
        const annualFields = [
          "grievanceMechanism",
          "dataPrivacyCompliance"
        ];

        // Monthly fields
        const monthlyFields = [
          "customerComplaints",
          "complaintsResolved",
          "productRecalls",
          "productRecallRate",
          "safetyIncidentsReported",
          "dataBreaches",
          "dataBreachRate",
          "marketingComplaints",
          "additionalDetails",
        ];

        Object.keys(fetchedData).forEach((key) => {
          if (annualFields.includes(key)) {
            annualData[key] = fetchedData[key];
          } else if (monthlyFields.includes(key)) {
            monthlyData[key] =
              key === "additionalDetails"
                ? fetchedData[key]
                : formatWithCommas(fetchedData[key]);
          }
        });
      }

      // Update annual form data with safe defaults for first visit/new entries
      setAnnualFormData((prevState) => {
        const grievanceRaw =
          annualData?.grievanceMechanism &&
          typeof annualData.grievanceMechanism === "object"
            ? annualData.grievanceMechanism
            : {};

        const toBool = (v, fallback = true) => {
          if (typeof v === "boolean") return v;
          if (typeof v === "string") {
            const normalized = v.trim().toLowerCase();
            if (normalized === "true") return true;
            if (normalized === "false") return false;
          }
          return fallback;
        };

        return {
          ...prevState,
          ...annualData,
          grievanceMechanism: {
            ...prevState.grievanceMechanism,
            ...grievanceRaw,
            status: toBool(grievanceRaw.status, prevState.grievanceMechanism?.status ?? true),
            remark:
              typeof grievanceRaw.remark === "string"
                ? grievanceRaw.remark
                : prevState.grievanceMechanism?.remark || "",
            documentUrl:
              typeof grievanceRaw.documentUrl === "string"
                ? grievanceRaw.documentUrl
                : prevState.grievanceMechanism?.documentUrl || "",
          },
          dataPrivacyCompliance: toBool(
            annualData?.dataPrivacyCompliance,
            prevState.dataPrivacyCompliance ?? true,
          ),
        };
      });

      // Update monthly form data
      setMonthlyFormData((prevState) => ({
        ...prevState,
        ...monthlyData,
      }));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData(id);
  }, [id, fetchData]);

  const handleBooleanChange = (e) => {
    const { name, value } = e.target;
    if (module === "annual") {
      setAnnualFormData((prev) => ({
        ...prev,
        [name]: value === "true",
      }));
    }
  };

  const handleNestedBooleanChange = (e) => {
    const { name, value } = e.target;
    setAnnualFormData((prev) => ({
      ...prev,
      grievanceMechanism: {
        ...prev.grievanceMechanism,
        [name]: value === "true",
      },
    }));
    setErrors((prev) => ({
      ...prev,
      grievanceMechanism: {
        ...prev?.grievanceMechanism,
        ...(value === "false" && { remark: "", documentUrl: "" }),
      },
    }));
  };

  const fetchTotalProduction = useCallback(async () => {
    const { companyId, year, month } = entryMeta;
    if (!companyId || !year || !month) {
      throw new Error("Missing companyId/year/month");
    }

    const res = await get(
      `social/dashboard/totalProduction?companyId=${companyId}&year=${year}&month=${month}`
    );
    // Extract totalQuantity from API response
    const raw = res?.data?.data?.totalQuantity ?? res?.data?.totalQuantity;
    const totalQty = Number(raw);
    if (Number.isNaN(totalQty)) {
      throw new Error("Invalid totalQuantity value from API");
    }
    return totalQty;
  }, [entryMeta]);

  // Fetch totalQuantity when entryMeta is set (for monthly module)
  useEffect(() => {
    if (module === "monthly" && entryMeta.companyId && entryMeta.year && entryMeta.month) {
      fetchTotalProduction()
        .then((qty) => {
          setTotalQuantity(qty);
          // Clear any previous error if fetch succeeds
          setErrors((prevErrors) => ({
            ...prevErrors,
            productRecallRate: "",
          }));
        })
        .catch((error) => {
          console.error("Failed to fetch total production:", error);
          setTotalQuantity(null);
          const backendMsg =
            error?.response?.data?.message ||
            "Failed to fetch total production";
          setErrors((prevErrors) => ({
            ...prevErrors,
            productRecallRate: backendMsg,
          }));
        });
    }
  }, [module, entryMeta, fetchTotalProduction]);

  // Recalculate productRecallRate when totalQuantity changes (if productRecalls has a value)
  useEffect(() => {
    if (module === "monthly" && totalQuantity && totalQuantity > 0) {
      const recallsNum = Number(removeCommas(monthlyFormData.productRecalls || 0));
      if (!Number.isNaN(recallsNum) && recallsNum > 0) {
        const rate = ((recallsNum / totalQuantity) * 100).toFixed(2);
        setMonthlyFormData((prev) => ({
          ...prev,
          productRecallRate: rate,
        }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          productRecallRate: "",
        }));
      }
    }
  }, [totalQuantity, module]);

  const handleNumberChange = async (e) => {
    const { name, value } = e.target;
    const normalizedValue = removeCommas(value);

    // dataBreachRate is auto-calculated and should not be manually edited
    if (name === "dataBreachRate") return;

    // Allow only digits for numeric fields
    if (name !== "additionalDetails" && !/^\d*$/.test(normalizedValue)) return;

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    setMonthlyFormData((prev) => {
      const updated = {
        ...prev,
        [name]:
          name === "additionalDetails"
            ? value
            : formatWithCommas(normalizedValue),
      };

      // Extract current values
      const complaints = Number(
        removeCommas(
          name === "customerComplaints" ? normalizedValue : prev.customerComplaints || 0,
        )
      );
      const resolved = Number(
        removeCommas(
          name === "complaintsResolved" ? normalizedValue : prev.complaintsResolved || 0,
        )
      );

      // Validation: resolved should not exceed complaints
      if (resolved > complaints) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          complaintsResolved:
            "Resolved complaints cannot exceed total complaints.",
        }));
        return prev;
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          complaintsResolved: "",
        }));
      }

      // Auto-calculate Data breach incidence Rate = dataBreaches / 30 * 100
      const breaches = Number(
        removeCommas(name === "dataBreaches" ? normalizedValue : prev.dataBreaches || 0)
      );
      if (!Number.isNaN(breaches) && breaches >= 0) {
        // dataBreachRate = (dataBreaches / 30) * 100
        const rate = ((breaches / 30) * 100).toFixed(2);
        updated.dataBreachRate = formatWithCommas(rate);
      }

      return updated;
    });

    // Auto-calculate Product recall rate using totalQuantity from state
    if (name === "productRecalls") {
      const recallsNum = Number(removeCommas(normalizedValue || 0));

      if (totalQuantity && totalQuantity > 0 && !Number.isNaN(recallsNum)) {
        const rate = ((recallsNum / totalQuantity) * 100).toFixed(2);
        setMonthlyFormData((prev) => ({
          ...prev,
          productRecalls: formatWithCommas(normalizedValue),
          productRecallRate: formatWithCommas(rate),
        }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          productRecallRate: "",
        }));
      } else {
        setMonthlyFormData((prev) => ({
          ...prev,
          productRecalls: formatWithCommas(normalizedValue),
          productRecallRate: "",
        }));
        // Don't set error here if totalQuantity is null - error is already set in useEffect
        if (totalQuantity === null) {
          // Error already set in useEffect, don't override it
        } else if (totalQuantity === 0 || Number.isNaN(totalQuantity)) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            productRecallRate: "Total quantity is zero or invalid for this period.",
          }));
        }
      }
    }
  };

  const handleSubmit = async () => {
    const hasErrors = validateForm();
    if (hasErrors) return;

    const formData = module === "annual" ? annualFormData : monthlyFormData;

    const payload = {
      customerSatisfaction: stripCommasFromNumericStrings(formData),
      stepsCompleted: {
        customerSatisfaction: true,
      },
    };

    await put(`/social/updateSocial?Id=${id}`, payload)
      .then((res) => {
        navigate("/social/data");
      })
      .catch((err) => {
        console.log(err);
        const backendMsg =
          err?.response?.data?.message ||
          "Failed to submit Customer Satisfaction data";

        setErrors((prevErrors) => ({
          ...prevErrors,
          productRecallRate: backendMsg,
        }));
      });
  };

  const monthlyFieldsConfig = [
    {
      label: "1. Number of Customer Complaints",
      name: "customerComplaints",
      placeholder: "Enter Number",
    },
    {
      label: "2. Number of Complaints Resolved",
      name: "complaintsResolved",
      placeholder: "Enter Number",
    },
    {
      label: "3. Number of Product Recalls",
      name: "productRecalls",
      placeholder: "Enter Number",
    },
    {
      label: "4. Product recall rate",
      name: "productRecallRate",
      placeholder: "Enter Number",
    },
    {
      label: "5. Number of Safety Incidents Reported",
      name: "safetyIncidentsReported",
      placeholder: "Enter Number",
    },
    {
      label: "6. Number of Data breaches",
      name: "dataBreaches",
      placeholder: "Enter Number",
    },
    {
      label: "7. Data breach incidence Rate",
      name: "dataBreachRate",
      placeholder: "Auto-calculated",
    },
    {
      label: "8. Number of Marketing Complaints",
      name: "marketingComplaints",
      placeholder: "Enter Number",
    },
  ];

  return (
    <MainDashboardLayout sidebar={<SocialLayout />}>
      {/* <div style={{ padding: "1rem 0" }}>
        <span
          style={{
            fontSize: "14px",
            fontWeight: "400",
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          Data{" "}
        </span>
        <span>
          <ChevronRightIcon />
        </span>{" "}
        <span
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "rgba(0, 0, 0, 1)",
          }}
        >
          Add New Entry
        </span>
      </div>
      <div
        style={{
          fontSize: "24px",
          fontWeight: "700",
          color: "#001632",
          marginBottom: "1rem",
        }}
      >
        Add New Entry
      </div> */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <p className={styles.form_headingStrongUpper}>H. CUSTOMER SATISFACTION</p>

        {module === "annual" ? (
          // Annual Module UI
          <>
            <BooleanRadioGroup
              label={"1. Grievance mechanism and redressal cell"}
              name={"status"}
              value={annualFormData?.grievanceMechanism?.status ?? true}
              onChange={handleNestedBooleanChange}
            />
            {annualFormData?.grievanceMechanism?.status && (
              <div style={{ marginBottom: "1rem", marginLeft: "1rem" }}>
                <p style={{ marginBottom: "0.5rem", fontSize: "14px" }}>
                  Provide in evidence.
                </p>
               
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    alignItems: "flex-start",
                    width: "50%",
                  }}
                >
                  <div
                    style={{
                     
                    }}
                  >
                    <FileUpload
                      setDocumentUrl={(url) => {
                        setAnnualFormData((prev) => ({
                          ...prev,
                          grievanceMechanism: {
                            ...prev.grievanceMechanism,
                            documentUrl: url,
                          },
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          grievanceMechanism: {
                            ...prev?.grievanceMechanism,
                            documentUrl: "",
                          },
                        }));
                      }}
                      filesList={annualFormData.grievanceMechanism.documentUrl}
                      mt="0px"
                      marginTop="0px"
                      isDisabled={true}
                    />
                    {errors?.grievanceMechanism?.documentUrl && (
                      <FormHelperText sx={{ color: "#D34040", fontSize: "14px" }}>
                        {errors.grievanceMechanism.documentUrl}
                      </FormHelperText>
                    )}
                  </div>
                  <TextField
                    name={"remark"}
                    multiline
                    minRows={1}
                    value={annualFormData.grievanceMechanism.remark}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      setAnnualFormData((prev) => ({
                        ...prev,
                        grievanceMechanism: {
                          ...prev.grievanceMechanism,
                          [name]: value,
                        },
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        grievanceMechanism: {
                          ...prev?.grievanceMechanism,
                          [name]: "",
                        },
                      }));
                    }}
                    variant="outlined"
                    type="text"
                    fullWidth
                    size="small"
                    error={!!errors?.grievanceMechanism?.remark}
                    helperText={errors?.grievanceMechanism?.remark}
                    placeholder={"Enter Additional Text"}
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "14px",
                        marginLeft: 0,
                        paddingLeft: "1px",
                      },
                    }}
                  />
                </div>
              </div>
            )}

            <BooleanRadioGroup
              label={"2. Compliance with Data Privacy Regulations"}
              name={"dataPrivacyCompliance"}
              value={annualFormData?.dataPrivacyCompliance ?? true}
              onChange={handleBooleanChange}
            />
          </>
        ) : (
          // Monthly Module UI
          <>
            {monthlyFieldsConfig.map((field, index) => (
              <div style={{ marginBottom: "1rem" }} key={index}>
                <p className={styles.form_heading} style={{ fontWeight: "bold", marginTop: "1rem" }}>
                  {field.label}
                </p>
                <TextField
                  name={field.name}
                  value={monthlyFormData[field.name] || ""}
                  onChange={handleNumberChange}
                  variant="outlined"
                  type="text"
                  fullWidth
                  size="small"
                  error={!!errors[field.name]}
                  helperText={errors[field.name]}
                  sx={{ pl: 2, width: "50%" }}
                  placeholder={field.placeholder}
                  disabled={field.name === "dataBreachRate" || field.name === "productRecallRate"}
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "14px",
                      marginLeft: 0,
                      paddingLeft: "1px",
                    },
                  }}
                />
              </div>
            ))}

            <div style={{ marginBottom: "1rem" }}>
              <p className={styles.form_heading} style={{ fontWeight: "bold", marginTop: "1rem",marginLeft:"17px" }}>
                Additional Details
              </p>
              <TextField
                name="additionalDetails"
                multiline
                value={monthlyFormData.additionalDetails || ""}
                onChange={handleNumberChange}
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.additionalDetails}
                helperText={errors.additionalDetails}
                sx={{ pl: 2, width: "50%" }}
                placeholder="Enter Additional Text"
                FormHelperTextProps={{
                  sx: {
                    fontSize: "14px",
                    marginLeft: 0,
                    paddingLeft: "1px",
                  },
                }}
              />
            </div>
          </>
        )}

        {/* Submit Button */}
        <Box
          sx={{
            width: "100%",
            mt: "10%",
            mb: 5,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            sx={backButtonSx}
            onClick={() => {
              console.log(module, "moduleInCustomersatisfaction");
              const entryTypeParam = module === "annual" ? "annual" : "monthly";
              console.log(entryTypeParam,module, "entryTypeParam+++");
              const basePath =
                module === "monthly"
                  ? "/social/data/training-development"
                  : "/social/data/employee-benefit";
              navigate(`${basePath}/${id}`);
            }}
          >
            <WestIcon sx={{ mr: 2 }} /> Back
          </Button>
          <Button
            variant="contained"
            className={styles.submit_button}
            onClick={handleSubmit}
            sx={primaryButtonSx}
          >
            Submit
            <ArrowForwardIcon sx={{ ml: 1 }} />   
          </Button>
        </Box>
      </div>
    </MainDashboardLayout>
  );
};

export default CustomerSatisfaction;
